import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stack, Label, TextField, PrimaryButton, DefaultButton, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { getModifySystemPromptData, updateSystemPrompt } from '../../../api/admin/api_admin';
import type { ModifySystemPromptData } from '../../../api/admin/models_admin';
import { AdminLabel, ErrorDialog, ConfirmDialog } from "../components";

export const UpdateSystemPrompt: React.FC = () => {
    const { seq } = useParams<{ seq: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<ModifySystemPromptData | null>(null);
    const [newPrompt, setNewPrompt] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!seq) {
                navigate('/menu/upload');
                return;
            }
            try {
                setLoading(true);
                const result = await getModifySystemPromptData(seq);
                if ("error" in result) {
                    setError(result.error);
                } else {
                    setData(result);
                    setNewPrompt(result.system_prompt ?? result.default_system_prompt);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [seq, navigate]);

    const handleSubmit = async () => {
        if (!seq || !newPrompt.trim()) {
            setError('新しいシステムプロンプトを入力してください');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);
            await updateSystemPrompt({
                category: seq,
                new_prompt: newPrompt,
            });
            setSuccess('システムプロンプトを更新しました');
            setTimeout(() => navigate('/menu/upload'), 1500);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'システムプロンプトの更新に失敗しました');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate('/menu/upload');
    };

    if (loading) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <Spinner label="読み込み中..." />
            </Stack>
        );
    }

    if (!data) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    データが見つかりません
                </MessageBar>
                <DefaultButton text="戻る" onClick={handleBack} styles={{ root: { marginTop: 20, width: 'fit-content' } }} />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ padding: 20, childrenGap: 20 }}>
            <AdminLabel text='システムプロンプト編集' />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            {
                <ConfirmDialog 
                    content={success ?? ""} 
                    onDismiss={() => setSuccess(null)} 
                    isOpen={Boolean(success)}
                    title="確認" />
            }

            <Stack tokens={{ childrenGap: 10 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Label styles={{ root: { width: 150, fontWeight: 600 } }}>カテゴリ名</Label>
                    <Label>{data.container_name}</Label>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Label styles={{ root: { width: 150, fontWeight: 600 } }}>現在のシステムプロンプト</Label>
                    <Label styles={{ root: { flex: 1 } }}>{data.system_prompt || '(未設定)'}</Label>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Label styles={{ root: { width: 150, fontWeight: 600 } }} required>
                        新しいシステムプロンプト
                    </Label>
                    <TextField
                        multiline
                        rows={15}
                        value={newPrompt}
                        onChange={(_, value) => setNewPrompt(value || '')}
                        styles={{ root: { flex: 1 }, field: { fontSize: 'large' } }}
                        required
                        autoComplete="off"
                    />
                </Stack>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text="登録" onClick={handleSubmit} disabled={submitting || !newPrompt.trim()} />
                <DefaultButton text="戻る" onClick={handleBack} disabled={submitting} />
            </Stack>
        </Stack>
    );
};